import { frenchTypography } from "./french-typography.js";
import {
  feeImpactPercentLabel,
  insurancePartRounded,
  mensualiteMirror,
  oneDecimalLabel,
  rateBelowGridEdge,
  ratePointsFrom,
  taegMirror,
  tenthPercent,
  wholePercent,
} from "./simulation.js";

/**
 * Round `L-1`, `M3` (`P1-LND-025`, `026`, `027`, `029`, `P1-BKW-009`;
 * WF-BNK-09; 02 §2.4.3's `revue` row; 02 §2.6.2's TAEG): **the composer's
 * arithmetic and its two verdicts, ONE truth for the door and for the screen**
 * , the policy check runs live while the analyst types (WF-BNK-09's
 * acceptance) on the same expression the door judges the save and the send
 * by. The hard blocks nothing justifies away (I28: the TAEG above the UEMOA
 * ceiling, the instalment above the file's capacity); the gaps a written
 * justification may carry to the chef or the committee (the rate under the
 * grid minus the tolerance, the duration above the policy's maximum, the
 * vehicle's age at maturity above the policy's, the LTV above the bank's own
 * ceiling). Pure: every figure comes in, nothing is read here.
 *
 * Money is integer FCFA; rates travel as one-decimal strings (« 10.9 ») and
 * are turned into numbers for the arithmetic alone. The instalment and the
 * TAEG are the mirrors' , the same expressions the buyer's screens compute
 * and `simulation`'s domain asserts against its own.
 */

/** What the composer reads of the bank's policy (05 §2.6.7, §2.6.8). */
export interface ComposerPolicy {
  readonly rateTolerancePct: string;
  readonly durationMaxMonths: number;
  readonly vehicleAgeAtMaturityMaxYears: number;
  readonly taegCeilingPct: string;
  readonly delegationThresholdAmount: number;
  readonly gradeRates: readonly { readonly grade: string; readonly ratePct: string }[];
}

/**
 * Round `L-5`, `M1` (WF-BNK-26 step 4 and 5, 05 §2.6.7 amended at `L-1b`): **the level each rule
 * carries, the drawing's constants and not columns** (`hakili-bank.jsx`, `chefPolitique.derog`).
 * `eliminatoire` is Hakili's upstream filter (the bank's criteria, `bank_criteria`): the file never
 * reaches the bank. The two derogable ones are the composer's gaps below, at the level named here
 * , ONE place, read by the gaps and by « Politique »'s selectors alike. A column per rule would be
 * a migration the architect orders.
 */
export const POLICY_RULE_LEVELS = {
  vehicleAgeAtGrant: "eliminatoire",
  vehicleAgeAtMaturity: "comiteCredit",
  ltv: "chefCredit",
  quotite: "eliminatoire",
} as const;

export type PolicyRuleLevel = (typeof POLICY_RULE_LEVELS)[keyof typeof POLICY_RULE_LEVELS];

/**
 * 05 §2.6.7: the committee sits after the client's acceptance above the threshold, strictly.
 * Moved here from `lending`'s domain at `U-2` so the underwriting run's offer step carries the
 * committee's flag by the same expression (« never a second arithmetic »); `lending` re-exports it.
 */
export const requiresCommittee = (amountFinanced: number, delegationThresholdAmount: number): boolean =>
  amountFinanced > delegationThresholdAmount;

/**
 * `COMMITTEE-ONE` (D-1025-9): **the quantity the delegation threshold is compared to is MONTANT_DOSSIER, the loan's capital: everything lent**, the
 * vehicle part, the fee each of the two the bank agreed to carry, and a financed warranty. One pure expression, beside `committeeReasonsOf`, that the
 * run's flag reads; lending's `amountBorrowedOf` (`client-offers.ts`) DELEGATES to it (`COMMITTEE-AMOUNT`), and the chef's three sites read the offer row through
 * lending's `rowBorrowedAmount`, which adapts the row to it: one arithmetic. A fee or a warranty that is not financed is not lent.
 */
export const borrowedAmountOf = (
  vehiclePart: number,
  bankFee: number,
  financesBankFee: boolean,
  hakiliFee: number,
  financesHakiliFee: boolean,
  warrantyAmount: number,
  financesWarranty: boolean,
): number =>
  vehiclePart +
  (financesBankFee ? bankFee : 0) +
  (financesHakiliFee ? hakiliFee : 0) +
  (financesWarranty ? warrantyAmount : 0);

/**
 * The rule each gap names, one wording for every reader: the composer's gaps, and the committee's reasons (a stored gap row carries the
 * code and the detail, never the label). `rule` below reads this map, so the two cannot drift.
 */
export const GAP_RULE_LABELS: Readonly<Record<Gap["code"], string>> = {
  rateBelowGrid: "Grille tarifaire",
  gridRateMissing: "Grille tarifaire",
  durationAboveMax: "Durée maximale",
  vehicleAgeAtMaturity: "Âge du véhicule à l'échéance",
  ltvAboveMax: "LTV sur la valeur expertisée",
};

/**
 * `COMITE-RECORD-1` (D-1024-7, Q1 and Q1b): **why a file sits before the committee, ONE expression for every reader.** The reasons
 * are the amount strictly above the threshold (05 §2.6.7, the `>` of `requiresCommittee`), then one per gap at the level
 * « Dérogeable · comité » in the order the gaps stand; no threshold means no amount reason. A file needs the committee when there is
 * at least one. The acceptance keeps this list as it stood (the policy may move after); the chef's queue draws one chip per reason.
 */
export type CommitteeReason =
  | { readonly kind: "amount"; readonly amountFinanced: number; readonly thresholdAmount: number }
  | {
      readonly kind: "rule";
      readonly code: Gap["code"];
      readonly rule: string;
      readonly detail: string;
    };

export const committeeReasonsOf = (
  amountFinanced: number,
  delegationThresholdAmount: number | null,
  gaps: readonly Pick<Gap, "code" | "detail" | "level">[],
): readonly CommitteeReason[] => [
  ...(delegationThresholdAmount !== null && requiresCommittee(amountFinanced, delegationThresholdAmount)
    ? [{ kind: "amount", amountFinanced, thresholdAmount: delegationThresholdAmount } as const]
    : []),
  ...gaps
    .filter((one) => one.level === "comiteCredit")
    .map((one) => ({
      kind: "rule" as const,
      code: one.code,
      rule: GAP_RULE_LABELS[one.code],
      detail: one.detail,
    })),
];

/** Whole years of loan: 36 → 3, 48 → 4, 60 → 5, no rounding needed for the drawing's three durations. One expression for the gap and the chef's row. */
export const vehicleAgeAtMaturityOf = (year: number, vehicleYear: number, durationMonths: number): number =>
  year - vehicleYear + Math.trunc((durationMonths + 6) / 12);

/** The drawing's three durations (WF-BNK-09 step 2); I1 caps the longest. */
export const COMPOSER_DURATIONS: readonly number[] = [36, 48, 60];

/** What the file, the policy and the ports give the composer , read by the door, never composed by a screen. */
export interface ComposerFacts {
  readonly amountFinanced: number;
  readonly depositPct: number | null;
  /** `SERVED-SHARE`: the share the underwriting prompt reads (the stored share while the price is unmoved, the recomputed one rounded down after a move); absent where a caller has no such fact. */
  readonly depositPctRead?: number | null;
  readonly durationMonths: number | null;
  /** The buyer's one choice for both fees (WF-CLI-10 §4), `null` while no mandate stands. */
  readonly feeFinancingRequested: boolean | null;
  readonly hakiliFeeAmount: number;
  readonly bankFeeAmount: number;
  /**
   * `CAPACITY-ONE`: the capacity of the file's own latest run (retained net x the bank POLICY's share, truncated to the
   * franc, less the retained commitments). The hard block reads THIS; `null` is « the run carries none » (an old run,
   * « opérandes non conservées », or none computed): I28 is never unchecked, the frozen figure below blocks instead
   * (never looser than before), and the recap says so. No grade, no offer (OFFER-GUARDS) is a different guard.
   */
  readonly capacityAmount: number | null;
  /** The figure the file froze at the submission (two-freezes ruling): printed « à la soumission », never a block. */
  readonly capacityAtSubmissionAmount: number;
  readonly netSalaryAmount: number;
  readonly existingCommitmentsAmount: number;
  readonly expertisedValueAmount: number | null;
  readonly vehicleYear: number | null;
  /** The bank's own LTV ceiling (`bank_criteria.ltv_max_pct`), `null` while none. */
  readonly ltvMaxPct: number | null;
  readonly borrowerInsuranceRatePct: number;
  readonly policy: ComposerPolicy | null;
  /**
   * The solidity grade Hakili gives the file (03 WF-BNK-27: « note attribuée
   * par Hakili »), through the port `OD-36` names; `null` until a door
   * computes one (phase 3). With no grade the grid is NOT compared , the
   * drawing's `politique.grille.B` was its one fixture's grade, not a rule ,
   * and the composer says so in words (R-A, 16 September).
   */
  readonly grade: string | null;
  /**
   * Position 59: **the file's own run has not ended** , its grade is not written yet. `grade` is `null` then, and the
   * composition says so in words (`GRADE_PENDING_NOTE`) and never as an absence of gap. A grade belongs to one
   * submission: another bank's letter is never read here. Absent (`undefined`) reads as `false`.
   */
  readonly gradePending?: boolean;
  /** The calendar year the age at maturity is counted from. */
  readonly year: number;
}

export interface ComposerInput {
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly financesBankFee: boolean;
  readonly financesHakiliFee: boolean;
  readonly justification: string;
}

export interface Block {
  readonly code: "taegAboveCeiling" | "instalmentAboveCapacity";
  readonly rule: string;
  readonly detail: string;
}

export interface Gap {
  readonly code:
    "rateBelowGrid" | "gridRateMissing" | "durationAboveMax" | "vehicleAgeAtMaturity" | "ltvAboveMax";
  readonly rule: string;
  readonly detail: string;
  readonly level: "chefCredit" | "comiteCredit";
}

export interface Recap {
  readonly totalFinancedAmount: number;
  readonly instalmentAmount: number;
  readonly insuranceInstalmentAmount: number;
  readonly capacityAmount: number | null;
  readonly capacityAtSubmissionAmount: number;
  /** Which figure the hard block and the used share read: the run's, or (the run carries none) the one frozen at the submission. */
  readonly capacityBasis: "run" | "soumission";
  readonly capacityUsedPct: number;
  readonly hakiliFeeAmount: number;
  readonly bankFeeAmount: number;
  readonly warrantyAmount: number;
  readonly taegPct: string;
  readonly underCeiling: boolean;
  readonly ltvVehiclePct: number | null;
  readonly ltvTotalPct: number | null;
  readonly gridRatePct: string | null;
  /**
   * `COMPOSER-GAPS` (S6, D2 frame 3x « 0,3 point sous la grille B+ »): the offer's nominal rate minus the grid's rate for the file's letter, in points, one decimal,
   * computed here and never by the screen: negative below the grid, `0` on it, positive above. `null` with no grid row for the letter, no letter, or no rate typed.
   */
  readonly gridDeltaPts: number | null;
  /** The letter `gridDeltaPts` is measured against; `null` with it. */
  readonly gridDeltaGrade: string | null;
  /** 02 §2.6.2's `impactFrais`, the fees' share amortised over the term, as the formula line prints it. */
  readonly feeImpactLabel: string;
}

export interface Composition {
  readonly input: ComposerInput;
  readonly recap: Recap;
  readonly blocks: readonly Block[];
  readonly gaps: readonly Gap[];
  readonly requiredLevel: "chefCredit" | "comiteCredit" | null;
  /** WF-BNK-09 step 5: a toggle diverges from the buyer's own choice. */
  readonly modified: boolean;
  readonly capitalDeltaAmount: number;
  readonly justificationRequired: boolean;
  readonly sendable: boolean;
  /** The door's sentence when the send may not go , the screen prints it under the blocks; `null` otherwise. */
  readonly refusal: string | null;
  /** `false` while no door has given the file a grade: the grid's rule is not evaluated, the screen says so. */
  readonly gridComparable: boolean;
  /** Position 59: the submission's run has not ended, so no gap was looked for; never to be read as « aucun écart ». */
  readonly gradePending: boolean;
  /** `OFFER-GUARDS`: no letter and no run pending (« note incomplète ») , the composition is answered, never written, never sent. */
  readonly gradeMissing: boolean;
  /** `GRADE_PENDING_NOTE` while `gradePending`, `null` otherwise. */
  readonly gradeNote: string | null;
}

/** Position 59 (round 33): the words of a composition made before the submission's analysis has ended. */
export const GRADE_PENDING_NOTE = frenchTypography("note en attente : l'analyse n'est pas terminée");

/**
 * `NOTE-EN-ATTENTE`: where the reason opens a line (the send's refusal), it is a sentence: capital, full stop. Inside a recap
 * (`gradeNote`) it stays the fragment above. Two constants, one source: this one is derived from the fragment, never retyped.
 */
export const GRADE_PENDING_REFUSAL = `${GRADE_PENDING_NOTE.charAt(0).toUpperCase()}${GRADE_PENDING_NOTE.slice(1)}.`;

/** `OFFER-GUARDS`: the send's sentence for a file whose run ended with no letter (« note incomplète »). */
export const NO_GRADE_REFUSAL = frenchTypography(
  "Aucune offre sans note : la note de ce dossier est incomplète.",
);

/**
 * `POLICY-FIRST` (D-1028-6): the bank side refuses any OFFER while the bank has no credit policy. One sentence, read by the analyst's
 * guard (`Composition.refusal`), by `POST /bank/files/:id/offer/submit` and by the chef's validation (`bank-decision.controller.ts`).
 */
export const NO_POLICY_REFUSAL = "Aucune politique de crédit n'est définie pour cette banque.";

/**
 * `OFFER-GUARDS` (I28, `ck_offers__taeg_ceiling`): **the UEMOA usury ceiling, a floor under every bank's own.** The
 * effective ceiling is the lower of the bank's policy ceiling and this one; with no policy, this one. A row above it
 * cannot exist, so the door says so in words before the database refuses it in none.
 */
export const TAEG_HARD_CEILING_PCT = 14;

const money = (amount: number): string => new Intl.NumberFormat("fr-FR").format(amount).replace(/\s/gu, " ");

/** The grid's rate for the file's grade; `null` where no grade is given or the grid has no row for it. */
export function gridRateFor(policy: ComposerPolicy, grade: string | null): string | null {
  if (grade === null) return null;
  return policy.gradeRates.find((one) => one.grade === grade)?.ratePct ?? null;
}

/**
 * `OD-37` round 8 (Q2) and `KIT-TOAST-D2` 3a: **the HIGHEST-RATE row of the bank's grid**, the one Capacité's instalment is taken at and the one
 * the Politique's save says it is when it is the row C. The highest rate wins; on a TIE the EARLIER row of the grid as stored keeps it (the strict
 * `>`), so a row C that only equals the highest is not « the highest ». `null` on an empty grid. ONE rule, read by the underwriting and by the
 * policy door alike: no screen compares rates itself (D-1020-7).
 */
export function highestGridRow<Row extends { readonly ratePct: string }>(rows: readonly Row[]): Row | null {
  return rows.reduce<Row | null>(
    (best, row) => (best === null || Number(row.ratePct) > Number(best.ratePct) ? row : best),
    null,
  );
}

export function compose(facts: ComposerFacts, input: ComposerInput): Composition {
  const financedFees =
    (input.financesBankFee ? facts.bankFeeAmount : 0) + (input.financesHakiliFee ? facts.hakiliFeeAmount : 0);
  const totalFinancedAmount = facts.amountFinanced + financedFees;
  const rate = Number(input.nominalRatePct);
  const instalment = mensualiteMirror(totalFinancedAmount, input.durationMonths, rate, {
    borrowerInsuranceRatePct: facts.borrowerInsuranceRatePct,
    effortCeilingPct: 0,
    effortComfortPct: 0,
  });
  const instalmentAmount = instalment?.amount ?? 0;
  const insuranceInstalmentAmount = instalment === null ? 0 : insurancePartRounded(instalment);
  // Only the buyer's fees enter, financed or not (02 §2.6.2's second property).
  const taegValue = taegMirror(
    rate,
    facts.hakiliFeeAmount + facts.bankFeeAmount,
    facts.amountFinanced,
    input.durationMonths,
  );
  const taegPct = oneDecimalLabel(taegValue ?? 0);
  const ceiling =
    facts.policy === null
      ? TAEG_HARD_CEILING_PCT
      : Math.min(Number(facts.policy.taegCeilingPct), TAEG_HARD_CEILING_PCT);
  const underCeiling = (taegValue ?? 0) <= ceiling;
  const capacityBasis = facts.capacityAmount === null ? "soumission" : "run";
  const blockingCapacity = facts.capacityAmount ?? facts.capacityAtSubmissionAmount;
  const capacityUsedPct = blockingCapacity > 0 ? tenthPercent(instalmentAmount, blockingCapacity) : 0;
  const ltvVehiclePct =
    facts.expertisedValueAmount === null || facts.expertisedValueAmount <= 0
      ? null
      : wholePercent(facts.amountFinanced, facts.expertisedValueAmount);
  const gridRatePct = facts.policy === null ? null : gridRateFor(facts.policy, facts.grade);
  const gridComparable = gridRatePct !== null;
  const rateTyped = input.nominalRatePct.trim() !== "" && Number.isFinite(rate);
  const gridDeltaPts = gridRatePct !== null && rateTyped ? ratePointsFrom(rate, Number(gridRatePct)) : null;
  const feeImpactLabel = feeImpactPercentLabel(
    facts.hakiliFeeAmount + facts.bankFeeAmount,
    facts.amountFinanced,
    input.durationMonths,
  );

  const blocks: Block[] = [];
  if (!underCeiling) {
    blocks.push({
      code: "taegAboveCeiling",
      rule: "Plafond d'usure UEMOA",
      detail: `TAEG de ${oneDecimalLabel(taegValue ?? 0).replace(".", ",")} % pour un plafond de ${oneDecimalLabel(ceiling).replace(".", ",")} %`,
    });
  }
  if (instalmentAmount > blockingCapacity) {
    blocks.push({
      code: "instalmentAboveCapacity",
      rule: "Capacité de remboursement",
      detail: `Mensualité de ${money(instalmentAmount)}\u00a0FCFA pour une capacité ${capacityBasis === "run" ? "" : "à la soumission "}de ${money(blockingCapacity)}\u00a0FCFA`,
    });
  }

  const gaps: Gap[] = [];
  const policy = facts.policy;
  if (policy !== null) {
    if (
      gridRatePct !== null &&
      facts.grade !== null &&
      rateBelowGridEdge(rate, Number(gridRatePct), Number(policy.rateTolerancePct))
    ) {
      const gap = oneDecimalLabel(Number(gridRatePct) - rate).replace(".", ",");
      gaps.push({
        code: "rateBelowGrid",
        rule: GAP_RULE_LABELS.rateBelowGrid,
        detail: `Taux de ${oneDecimalLabel(rate).replace(".", ",")} % contre ${oneDecimalLabel(Number(gridRatePct)).replace(".", ",")} % pour une note ${facts.grade}, soit ${gap} point d'écart`,
        level: "chefCredit",
      });
    }
    // `OFFER-GUARDS`: a letter with no row in the grid is a gap the chef decides, never a silent « not comparable ».
    if (gridRatePct === null && facts.grade !== null) {
      gaps.push({
        code: "gridRateMissing",
        rule: GAP_RULE_LABELS.gridRateMissing,
        detail: `aucun taux pour la note ${facts.grade}`,
        level: "chefCredit",
      });
    }
    if (input.durationMonths > policy.durationMaxMonths) {
      gaps.push({
        code: "durationAboveMax",
        rule: GAP_RULE_LABELS.durationAboveMax,
        detail: `Durée de ${String(input.durationMonths)} mois contre un maximum de ${String(policy.durationMaxMonths)} mois`,
        level: "chefCredit",
      });
    }
    if (facts.vehicleYear !== null) {
      const ageAtMaturity = vehicleAgeAtMaturityOf(facts.year, facts.vehicleYear, input.durationMonths);
      if (ageAtMaturity > policy.vehicleAgeAtMaturityMaxYears) {
        gaps.push({
          code: "vehicleAgeAtMaturity",
          rule: GAP_RULE_LABELS.vehicleAgeAtMaturity,
          detail: `Véhicule de ${String(ageAtMaturity)} ans en fin de prêt, contre une limite de ${String(policy.vehicleAgeAtMaturityMaxYears)} ans`,
          level: POLICY_RULE_LEVELS.vehicleAgeAtMaturity,
        });
      }
    }
  }
  if (ltvVehiclePct !== null && facts.ltvMaxPct !== null && ltvVehiclePct > facts.ltvMaxPct) {
    gaps.push({
      code: "ltvAboveMax",
      rule: GAP_RULE_LABELS.ltvAboveMax,
      detail: `LTV de ${String(ltvVehiclePct)} % contre un maximum de ${String(facts.ltvMaxPct)} %`,
      // The drawing's level (`chefPolitique.derog.ltv: "chef"`), 05 §2.6.7 amended at `L-1b`.
      level: POLICY_RULE_LEVELS.ltv,
    });
  }
  const requiredLevel = gaps.some((one) => one.level === "comiteCredit")
    ? "comiteCredit"
    : gaps.length > 0
      ? "chefCredit"
      : null;

  // WF-BNK-09 step 5: the buyer asked one thing for both fees; a toggle that
  // answers otherwise is a modification, and the delta is the capital added.
  const asked = facts.feeFinancingRequested === true;
  const modified = input.financesBankFee !== asked || input.financesHakiliFee !== asked;
  const askedCapital = asked ? facts.bankFeeAmount + facts.hakiliFeeAmount : 0;
  const capitalDeltaAmount = financedFees - askedCapital;
  const justificationRequired = gaps.length > 0 && input.justification === "";
  const gradePending = facts.gradePending === true;
  const gradeMissing = facts.grade === null && !gradePending;
  const refusal =
    facts.policy === null
      ? NO_POLICY_REFUSAL
      : blocks.length > 0
        ? "Une règle éliminatoire ne peut pas faire l'objet d'une dérogation. Ajustez les conditions pour poursuivre."
        : gradeMissing
          ? NO_GRADE_REFUSAL
          : gradePending
            ? GRADE_PENDING_REFUSAL
            : justificationRequired
              ? "Une dérogation exige une justification écrite avant l'envoi."
              : null;

  return {
    input,
    recap: {
      totalFinancedAmount,
      instalmentAmount,
      insuranceInstalmentAmount,
      capacityAmount: facts.capacityAmount,
      capacityAtSubmissionAmount: facts.capacityAtSubmissionAmount,
      capacityBasis,
      capacityUsedPct,
      hakiliFeeAmount: facts.hakiliFeeAmount,
      bankFeeAmount: facts.bankFeeAmount,
      warrantyAmount: 0,
      taegPct,
      underCeiling,
      ltvVehiclePct,
      ltvTotalPct: ltvVehiclePct,
      gridRatePct,
      gridDeltaPts,
      gridDeltaGrade: gridDeltaPts === null ? null : facts.grade,
      feeImpactLabel,
    },
    // `FRENCH-SPACING`: the details and the refusal carry French typography at source (display only).
    blocks: blocks.map((one) => ({ ...one, detail: frenchTypography(one.detail) })),
    gaps: gaps.map((one) => ({ ...one, detail: frenchTypography(one.detail) })),
    requiredLevel,
    modified,
    capitalDeltaAmount,
    justificationRequired,
    sendable: refusal === null,
    refusal: refusal === null ? null : frenchTypography(refusal),
    gridComparable,
    gradePending,
    gradeMissing,
    gradeNote: gradePending ? GRADE_PENDING_NOTE : null,
  };
}
